/**
 * Spend Easy - Shared Rich Demo Dataset
 * Generates 35-45 realistic transactions per month across the last 3 months
 * with accounts, budgets, bills, subscriptions, goals, and family hubs.
 */

import { subMonths, subDays } from 'date-fns';

const now = new Date();

export const INITIAL_DEMO_ACCOUNTS = [
  { id: 'acc-1', name: 'Main Checking', type: 'bank', initialBalance: 45000, createdAt: subMonths(now, 3) },
  { id: 'acc-2', name: 'Cash', type: 'cash', initialBalance: 4500, createdAt: subMonths(now, 3) },
  { id: 'acc-3', name: 'Emergency Savings', type: 'savings', initialBalance: 95000, createdAt: subMonths(now, 3) },
  { id: 'acc-4', name: 'Investment Portfolio', type: 'investment', initialBalance: 140000, createdAt: subMonths(now, 3) },
  { id: 'acc-5', name: 'Credit Card', type: 'card', initialBalance: 0, createdAt: subMonths(now, 3) },
];

export const INITIAL_DEMO_BUDGETS = {
  'Food & Dining': 9500,
  'Groceries': 12000,
  'Transportation': 6500,
  'Housing & Utilities': 28000,
  'Shopping': 8500,
  'Entertainment': 4500,
  'Healthcare': 5000,
  'Bills & Fees': 5500,
};

export const INITIAL_DEMO_GOALS = [
  {
    id: 'goal-1',
    name: 'Emergency Fund (6 Months)',
    targetAmount: 180000,
    currentAmount: 135000,
    targetDate: new Date(now.getFullYear(), now.getMonth() + 6, 1),
    linkedAccount: 'Emergency Savings',
    color: '#10B981',
    isCompleted: false,
    createdAt: subMonths(now, 4),
  },
  {
    id: 'goal-2',
    name: 'MacBook Pro M3 Max',
    targetAmount: 220000,
    currentAmount: 165000,
    targetDate: new Date(now.getFullYear(), now.getMonth() + 3, 15),
    linkedAccount: 'Main Checking',
    color: '#3B82F6',
    isCompleted: false,
    createdAt: subMonths(now, 2),
  },
  {
    id: 'goal-3',
    name: 'Japan Autumn Vacation',
    targetAmount: 120000,
    currentAmount: 120000,
    targetDate: subDays(now, 10),
    linkedAccount: 'Emergency Savings',
    color: '#F59E0B',
    isCompleted: true,
    createdAt: subMonths(now, 5),
  },
];

export const INITIAL_DEMO_BILLS = [
  {
    id: 'bill-1',
    name: 'Gigabit Fiber Internet',
    amount: 1450,
    account: 'Main Checking',
    dueDate: new Date(now.getFullYear(), now.getMonth(), 10),
    repeat: 'monthly',
    isPaid: false,
    createdAt: subMonths(now, 2),
  },
  {
    id: 'bill-2',
    name: 'Apartment Monthly Lease',
    amount: 22000,
    account: 'Main Checking',
    dueDate: new Date(now.getFullYear(), now.getMonth(), 5),
    repeat: 'monthly',
    isPaid: true,
    lastPaidDate: subDays(now, 3),
    createdAt: subMonths(now, 3),
  },
  {
    id: 'bill-3',
    name: 'Mobile Postpaid Plan',
    amount: 899,
    account: 'Credit Card',
    dueDate: new Date(now.getFullYear(), now.getMonth(), 18),
    repeat: 'monthly',
    isPaid: false,
    createdAt: subMonths(now, 1),
  },
  {
    id: 'bill-4',
    name: 'City Power & Electric Utility',
    amount: 2750,
    account: 'Main Checking',
    dueDate: new Date(now.getFullYear(), now.getMonth(), 12),
    repeat: 'monthly',
    isPaid: false,
    createdAt: subMonths(now, 2),
  },
];

export const INITIAL_DEMO_SUBSCRIPTIONS = [
  {
    id: 'sub-1',
    name: 'Netflix 4K UHD Premium',
    amount: 649,
    category: 'Entertainment',
    billingCycle: 'monthly',
    account: 'Credit Card',
    startDate: subMonths(now, 10),
    nextRenewalDate: new Date(now.getFullYear(), now.getMonth(), 14),
    isActive: true,
    createdAt: subMonths(now, 10),
  },
  {
    id: 'sub-2',
    name: 'Spotify Family Plan',
    amount: 179,
    category: 'Entertainment',
    billingCycle: 'monthly',
    account: 'Credit Card',
    startDate: subMonths(now, 12),
    nextRenewalDate: new Date(now.getFullYear(), now.getMonth(), 22),
    isActive: true,
    createdAt: subMonths(now, 12),
  },
  {
    id: 'sub-3',
    name: 'Equinox Athletic Club',
    amount: 2400,
    category: 'Healthcare',
    billingCycle: 'monthly',
    account: 'Main Checking',
    startDate: subMonths(now, 6),
    nextRenewalDate: new Date(now.getFullYear(), now.getMonth(), 11),
    isActive: true,
    createdAt: subMonths(now, 6),
  },
  {
    id: 'sub-4',
    name: 'ChatGPT Plus & AI Suite',
    amount: 1999,
    category: 'Bills & Fees',
    billingCycle: 'monthly',
    account: 'Credit Card',
    startDate: subMonths(now, 4),
    nextRenewalDate: new Date(now.getFullYear(), now.getMonth(), 25),
    isActive: true,
    createdAt: subMonths(now, 4),
  },
];

export const INITIAL_DEMO_FAMILY = {
  id: 'family-demo-1',
  name: 'Demo Family Hub',
  ownerId: 'demo_guest_user',
  memberIds: ['demo_guest_user', 'member_2'],
  memberRoles: {
    demo_guest_user: 'owner',
    member_2: 'member',
  },
  memberMeta: {
    demo_guest_user: {
      displayName: 'Demo User (You)',
      email: 'demo@spendeasy.app',
      role: 'owner',
    },
    member_2: {
      displayName: 'Alex Smith',
      email: 'alex@example.com',
      role: 'member',
    },
  },
};

/**
 * Generates 38 realistic transactions per month for 3 consecutive months:
 * Month 0 (Current Month), Month -1 (1 month ago), Month -2 (2 months ago).
 * Total: 114 transactions.
 */
export function generateThreeMonthDemoTransactions(baseDate = new Date()) {
  const transactions = [];

  // Template of 38 realistic monthly transactions
  const monthTemplates = [
    { day: 1, hour: 9, min: 0, type: 'income', amount: 78500, category: 'Salary', categoryIcon: '💰', account: 'Main Checking', description: 'Monthly Corporate Salary Deposit' },
    { day: 1, hour: 11, min: 30, type: 'expense', amount: 22000, category: 'Housing & Utilities', categoryIcon: '🏠', account: 'Main Checking', description: 'Apartment Monthly Lease Rent' },
    { day: 1, hour: 14, min: 15, type: 'expense', amount: 320, category: 'Food & Dining', categoryIcon: '☕', account: 'Cash', description: 'Espresso & Butter Croissant' },
    { day: 2, hour: 10, min: 0, type: 'expense', amount: 3850, category: 'Groceries', categoryIcon: '🛒', account: 'Credit Card', description: 'Trader Joe’s Weekly Pantry Haul' },
    { day: 2, hour: 13, min: 30, type: 'expense', amount: 420, category: 'Food & Dining', categoryIcon: '🥗', account: 'Main Checking', description: 'Mediterranean Lunch Bowl' },
    { day: 2, hour: 18, min: 45, type: 'expense', amount: 280, category: 'Transportation', categoryIcon: '🚇', account: 'Cash', description: 'Metro Transit Card Pass' },
    { day: 3, hour: 10, min: 30, type: 'transfer', amount: 12000, category: 'Transfer', categoryIcon: '🔄', account: 'Main Checking', toAccount: 'Emergency Savings', description: 'Monthly Automated Savings Allocation' },
    { day: 3, hour: 15, min: 20, type: 'expense', amount: 1450, category: 'Bills & Fees', categoryIcon: '📶', account: 'Main Checking', description: 'High-Speed Gigabit Fiber Internet' },
    { day: 4, hour: 11, min: 0, type: 'expense', amount: 2750, category: 'Housing & Utilities', categoryIcon: '⚡', account: 'Main Checking', description: 'City Power & Electric Utility' },
    { day: 4, hour: 14, min: 0, type: 'expense', amount: 850, category: 'Food & Dining', categoryIcon: '🍣', account: 'Credit Card', description: 'Sushi Bento Business Lunch' },
    { day: 5, hour: 9, min: 15, type: 'expense', amount: 450, category: 'Transportation', categoryIcon: '🚗', account: 'Credit Card', description: 'Uber Commute Downtown' },
    { day: 5, hour: 17, min: 30, type: 'expense', amount: 1950, category: 'Groceries', categoryIcon: '🥑', account: 'Credit Card', description: 'Whole Foods Organic Produce' },
    { day: 6, hour: 12, min: 0, type: 'income', amount: 18500, category: 'Freelance', categoryIcon: '💻', account: 'Main Checking', description: 'UI/UX Design Sprint Milestone' },
    { day: 6, hour: 19, min: 30, type: 'expense', amount: 2100, category: 'Food & Dining', categoryIcon: '🍕', account: 'Credit Card', description: 'Artisan Woodfire Pizza Dinner' },
    { day: 7, hour: 8, min: 30, type: 'expense', amount: 649, category: 'Entertainment', categoryIcon: '🎬', account: 'Credit Card', description: 'Netflix 4K UHD Subscription' },
    { day: 7, hour: 9, min: 0, type: 'expense', amount: 179, category: 'Entertainment', categoryIcon: '🎵', account: 'Credit Card', description: 'Spotify Family Plan' },
    { day: 8, hour: 11, min: 45, type: 'expense', amount: 3450, category: 'Shopping', categoryIcon: '📦', account: 'Credit Card', description: 'Amazon Home Office Supplies' },
    { day: 8, hour: 16, min: 0, type: 'expense', amount: 3100, category: 'Transportation', categoryIcon: '⛽', account: 'Credit Card', description: 'Shell V-Power Gasoline Fuel' },
    { day: 9, hour: 13, min: 15, type: 'expense', amount: 580, category: 'Healthcare', categoryIcon: '💊', account: 'Cash', description: 'CVS Pharmacy Prescription & Vitamins' },
    { day: 9, hour: 20, min: 0, type: 'expense', amount: 1650, category: 'Entertainment', categoryIcon: '🍿', account: 'Credit Card', description: 'IMAX Cinema Tickets & Snacks' },
    { day: 10, hour: 10, min: 0, type: 'transfer', amount: 5000, category: 'Transfer', categoryIcon: '🔄', account: 'Main Checking', toAccount: 'Cash', description: 'ATM Cash Withdrawal' },
    { day: 10, hour: 14, min: 30, type: 'expense', amount: 350, category: 'Food & Dining', categoryIcon: '☕', account: 'Cash', description: 'Matcha Latte & Muffin' },
    { day: 11, hour: 7, min: 30, type: 'expense', amount: 2400, category: 'Healthcare', categoryIcon: '🏋️', account: 'Main Checking', description: 'Equinox Athletic Club Monthly Fee' },
    { day: 11, hour: 16, min: 20, type: 'expense', amount: 1250, category: 'Groceries', categoryIcon: '🥖', account: 'Credit Card', description: 'Artisan Bakery & Deli Provisions' },
    { day: 12, hour: 12, min: 15, type: 'expense', amount: 890, category: 'Education', categoryIcon: '📚', account: 'Credit Card', description: 'O’Reilly Tech Books & Documentation' },
    { day: 12, hour: 18, min: 0, type: 'expense', amount: 750, category: 'Personal Care', categoryIcon: '✨', account: 'Cash', description: 'Barbershop Haircut & Styling' },
    { day: 13, hour: 13, min: 45, type: 'expense', amount: 480, category: 'Food & Dining', categoryIcon: '🍔', account: 'Main Checking', description: 'Shake Shack Burger & Shake' },
    { day: 13, hour: 19, min: 30, type: 'expense', amount: 3200, category: 'Food & Dining', categoryIcon: '🍷', account: 'Credit Card', description: 'Weekend Italian Trattoria Dining' },
    { day: 14, hour: 11, min: 0, type: 'expense', amount: 899, category: 'Bills & Fees', categoryIcon: '📱', account: 'Credit Card', description: 'Unlimited 5G Mobile Service' },
    { day: 14, hour: 15, min: 30, type: 'expense', amount: 1680, category: 'Shopping', categoryIcon: '👕', account: 'Credit Card', description: 'Uniqlo Cotton Basics & Apparel' },
    { day: 15, hour: 10, min: 0, type: 'income', amount: 4500, category: 'Investments', categoryIcon: '📈', account: 'Investment Portfolio', description: 'Quarterly Index Fund Dividends' },
    { day: 15, hour: 10, min: 30, type: 'transfer', amount: 15000, category: 'Transfer', categoryIcon: '🔄', account: 'Main Checking', toAccount: 'Investment Portfolio', description: 'S&P 500 Index Dollar-Cost Averaging' },
    { day: 16, hour: 12, min: 45, type: 'expense', amount: 1850, category: 'Healthcare', categoryIcon: '🦷', account: 'Credit Card', description: 'Dental Hygiene Routine Checkup' },
    { day: 17, hour: 14, min: 0, type: 'expense', amount: 520, category: 'Transportation', categoryIcon: '🚗', account: 'Credit Card', description: 'Lyft Airport Rideshare' },
    { day: 18, hour: 11, min: 30, type: 'expense', amount: 2650, category: 'Shopping', categoryIcon: '🎧', account: 'Credit Card', description: 'Bose ANC Headphones Replacement Pad' },
    { day: 19, hour: 16, min: 45, type: 'expense', amount: 1420, category: 'Groceries', categoryIcon: '🍓', account: 'Credit Card', description: 'Farmers Market Fresh Berries' },
    { day: 20, hour: 18, min: 0, type: 'transfer', amount: 16000, category: 'Transfer', categoryIcon: '🔄', account: 'Main Checking', toAccount: 'Credit Card', description: 'Credit Card Statement Full Payment' },
    { day: 21, hour: 13, min: 0, type: 'income', amount: 7500, category: 'Freelance', categoryIcon: '💼', account: 'Main Checking', description: 'Consulting Advisory Retainer' },
  ];

  // Generate 3 months: m = 0 (current), m = 1 (1 month ago), m = 2 (2 months ago)
  [0, 1, 2].forEach((m) => {
    const targetMonthDate = subMonths(baseDate, m);
    const targetYear = targetMonthDate.getFullYear();
    const targetMonth = targetMonthDate.getMonth();

    monthTemplates.forEach((item, idx) => {
      // Slight variation in amount across months (+/- 5%)
      const variationFactor = m === 0 ? 1 : m === 1 ? 0.96 : 1.04;
      const roundedAmount = Math.round(item.amount * variationFactor);

      const txDate = new Date(targetYear, targetMonth, item.day, item.hour, item.min);

      transactions.push({
        id: `tx-m${m}-${idx + 1}`,
        type: item.type,
        amount: roundedAmount,
        category: item.category,
        categoryIcon: item.categoryIcon,
        account: item.account,
        ...(item.toAccount ? { toAccount: item.toAccount } : {}),
        date: txDate,
        description: item.description,
      });
    });
  });

  // Sort descending by date
  return transactions.sort((a, b) => b.date - a.date);
}

export const INITIAL_DEMO_TRANSACTIONS = generateThreeMonthDemoTransactions(now);
